import { last } from '@quromedical/utils';
import { useCallback, useRef, useState } from 'react';
import useSWR from 'swr';

interface PaginatedDataResult<TData> {
  hasNext: boolean;
  hasPrev: boolean;
  /**
   * This is the data for the currently loaded page.
   */
  data?: TData[];
  error: unknown;
  isLoading: boolean;
  page: number;
  loadNext: () => Promise<void>;
  loadPrev: () => Promise<void>;
  setCount: (count: number) => void;
  resetPage: () => void;
  setPage: (page: number) => void;
}

export type FetcherOptions = {
  count?: number;
  nextToken?: string;
  page?: number;
};

export type PageFetcher<TResponse> = (
  options: FetcherOptions,
) => Promise<TResponse>;

export const getNextTokenFromLink = (
  link: fhir4.BundleLink[] = [],
): string | undefined => {
  const token = link.find((l) => l.relation === 'next');
  return token?.url;
};

export const getField =
  <TData extends {}, TKey extends keyof TData>(key: TKey) =>
  (data: TData): TData[TKey] =>
    data[key];

const getPaginatedSWRToken = (
  key: string,
  count: number | undefined,
  activeToken: string | undefined,
): string =>
  `paginated-key/${key}/token=${activeToken || 'unset'}/count=${count || 'unset'}`;

export const usePaginatedData = <TResponse, TData>(
  key: string,
  fetcher: PageFetcher<TResponse>,
  getDataFromResponse: (data: TResponse) => TData[],
  getNextToken: (prevData: TResponse) => string | undefined,
  initialCount?: number,
  initialPage = 1,
): PaginatedDataResult<TData> => {
  const [count, setCount] = useState(initialCount);
  const [prevTokens, setPrevTokens] = useState<string[]>([]);
  const [activeToken, setActiveToken] = useState<string>();
  const [page, setPage] = useState(initialPage);

  const lastResponseRef = useRef<TResponse | undefined>(undefined);

  const keyedFetcher = useCallback(async () => {
    const res = await fetcher({ nextToken: activeToken, count, page });
    lastResponseRef.current = res;
    return res;
  }, [activeToken, count, page, fetcher]);

  const swrKey = getPaginatedSWRToken(key, count, activeToken);

  const { data, error, isValidating } = useSWR<TResponse, unknown>(
    swrKey,
    keyedFetcher,
  );

  const responseData = data ? getDataFromResponse(data) : undefined;

  const nextToken = data ? getNextToken(data) : undefined;

  const loadNext = useCallback(async () => {
    if (nextToken) {
      const newPage = page + 1;
      setActiveToken(nextToken);
      setPrevTokens([...prevTokens, nextToken]);
      setPage(newPage);

      await fetcher({
        nextToken,
        page: newPage,
        count,
      });
    }
  }, [nextToken, prevTokens, page, fetcher, count]);

  const prevToken = last(prevTokens);

  const loadPrev = useCallback(async () => {
    const remainingTokens = prevTokens.slice(0, -1);
    const newPage = Math.max(1, page - 1);

    setActiveToken(() => last(remainingTokens));
    setPrevTokens(remainingTokens);
    setPage(newPage);

    await fetcher({
      nextToken: last(remainingTokens),
      page: newPage,
      count,
    });
  }, [prevTokens, page, fetcher, count]);

  const onCountChange = useCallback((value: number) => {
    setActiveToken(undefined);
    setCount(value);
    setPage(1);
  }, []);

  const resetPage = useCallback(() => {
    setActiveToken(undefined);
    setPrevTokens([]);
    setPage(1);
  }, []);

  return {
    error,
    page,
    data: responseData,
    isLoading: isValidating,
    hasNext: !!nextToken,
    hasPrev: !!prevToken,
    loadNext,
    loadPrev,
    setCount: onCountChange,
    resetPage,
    setPage,
  };
};
